import { gql } from "@apollo/client";
import { BaseMutationTuple, jssaQuery, useBaseMutation } from "./index";
import { CostMetadata } from "../../../common/models";
import { CostMetadataInput, CostMetadataCreateInput, CostMetadataUpdateInput } from "../../../common/inputs/CostMetadataInput";
import { costMetadataAllFieldsFragment } from "./CommonQueries";

export const costMetadataByShopifyProductIdQuery = gql`
    ${costMetadataAllFieldsFragment}
    query costMetadataByShopifyProductId(
        $shopifyProductId: String!,
        $variantId: String,
        $startDateFrom: DateTime,
        $startDateTo: DateTime,
        $options: CostMetadataInput
    ) {
        costMetadataByShopifyProductId(
            shopifyProductId: $shopifyProductId,
            variantId: $variantId,
            startDateFrom: $startDateFrom,
            startDateTo: $startDateTo,
            options: $options
        ) {
            ...CostMetadataAllFields
        }
    }
`;

export const createCostMetadataMutation = gql`
    ${costMetadataAllFieldsFragment}
    mutation createCostMetadata($data: CostMetadataCreateInput!) {
        createCostMetadata(data: $data) {
            ...CostMetadataAllFields
        }
    }
`;

export const updateCostMetadataMutation = gql`
    ${costMetadataAllFieldsFragment}
    mutation updateCostMetadata($data: CostMetadataUpdateInput!) {
        updateCostMetadata(data: $data) {
            ...CostMetadataAllFields
        }
    }
`;

export const useCreateCostMetadataMutation = (): BaseMutationTuple<CostMetadataCreateInput, CostMetadata> => {
    const transformInput = (input: CostMetadataCreateInput) => {
        return { data: input };
    };

    const transformOutput = (output: any) => {
        return output.createCostMetadata;
    };

    return useBaseMutation<CostMetadataCreateInput, CostMetadata>(
        createCostMetadataMutation,
        transformInput,
        transformOutput,
    );
};

export const useUpdateCostMetadataMutation = (): BaseMutationTuple<CostMetadataUpdateInput, CostMetadata> => {
    const transformInput = (input: CostMetadataUpdateInput) => {
        const cleaned = { ...input } as any;
        delete cleaned.createdAt;
        delete cleaned.updatedAt;
        delete cleaned.shopifyProductVariant;
        delete cleaned.costPayoutModifier;
        return { data: cleaned };
    };

    const transformOutput = (output: any) => {
        return output.updateCostMetadata;
    };

    return useBaseMutation<CostMetadataUpdateInput, CostMetadata>(
        updateCostMetadataMutation,
        transformInput,
        transformOutput,
    );
};

export interface CostMetadataQueryFilters {
    shopifyProductId: string;
    variantId?: string;
    startDateFrom?: string;
    startDateTo?: string;
    options?: CostMetadataInput;
}

export const queryCostMetadataByShopifyProductId = async (
    filters: CostMetadataQueryFilters
): Promise<CostMetadata[]> => {
    const { data: { costMetadataByShopifyProductId } } = await jssaQuery({
        query: costMetadataByShopifyProductIdQuery,
        variables: {
            shopifyProductId: filters.shopifyProductId,
            variantId: filters.variantId,
            startDateFrom: filters.startDateFrom,
            startDateTo: filters.startDateTo,
            options: filters.options,
        },
    });
    return costMetadataByShopifyProductId;
};
